'use client'

import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Loader2,
  Mail,
  AlertCircle,
  Calendar,
  RefreshCw,
  FileText,
  ArrowLeft,
  Settings2,
  Sparkles,
  CheckCircle2,
} from 'lucide-react'
import { toast } from 'sonner'

type ModalStep = 'config' | 'prompt' | 'result'

interface Inbox {
  id: string
  address: string
}

interface CachedPrompt {
  prompt: string
  stats: {
    emailsFound: number
    emailsProcessed: number
    threadsAnalyzed: number
    tokensUsed: number
  }
  generatedAt: string
  expiresAt: string
  inboxId: string
}

interface GeneratePromptModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onApplyPrompt: (prompt: string) => void
}

export function GeneratePromptModal({
  open,
  onOpenChange,
  onApplyPrompt,
}: GeneratePromptModalProps) {
  const { fetchWithAuth } = useAuth()

  const [step, setStep] = useState<ModalStep>('config')
  const [inboxes, setInboxes] = useState<Inbox[]>([])
  const [selectedInboxId, setSelectedInboxId] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [maxEmails, setMaxEmails] = useState<string>('1000')
  const [emailCountPreview, setEmailCountPreview] = useState<{ count: number; hasMore: boolean } | null>(null)
  const [isLoadingInboxes, setIsLoadingInboxes] = useState(false)
  const [isLoadingCount, setIsLoadingCount] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // Prompt state
  const [systemPrompt, setSystemPrompt] = useState<string>('')
  const [promptSource, setPromptSource] = useState<'tenant' | 'global' | 'default'>('default')
  // Generated prompt state
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('')
  const [generatedStats, setGeneratedStats] = useState<CachedPrompt['stats'] | null>(null)

  // Cached prompt state
  const [cachedPrompt, setCachedPrompt] = useState<CachedPrompt | null>(null)

  // Set default date to 30 days ago
  useEffect(() => {
    if (open && !dateFrom) {
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
      setDateFrom(thirtyDaysAgo.toISOString().split('T')[0])
    }
  }, [open, dateFrom])

  useEffect(() => {
    if (open) {
      fetchInboxesAndData()
    }
  }, [open])

  // Fetch email count when params change
  useEffect(() => {
    if (selectedInboxId && dateFrom) {
      fetchEmailCount()
    } else {
      setEmailCountPreview(null)
    }
  }, [selectedInboxId, dateFrom, dateTo])

  const fetchInboxesAndData = async () => {
    try {
      setIsLoadingInboxes(true)
      const res = await fetchWithAuth('/api/agents/email/auto-reply/generate-prompt')
      if (!res.ok) throw new Error('Failed to fetch data')
      const data = await res.json()
      setInboxes(data.inboxes || [])
      setSystemPrompt(data.system_prompt || '')
      setPromptSource(data.prompt_source || 'default')

      // Check for cached prompt
      if (data.cached_prompt) {
        setCachedPrompt(data.cached_prompt)
      }

      if (data.inboxes?.length > 0 && !selectedInboxId) {
        setSelectedInboxId(data.inboxes[0].id)
      }
    } catch (error) {
      console.error('Failed to fetch inboxes:', error)
      toast.error('Failed to load email inboxes')
    } finally {
      setIsLoadingInboxes(false)
    }
  }

  const fetchEmailCount = useCallback(async () => {
    if (!selectedInboxId || !dateFrom) return

    try {
      setIsLoadingCount(true)
      const params = new URLSearchParams({
        inbox_id: selectedInboxId,
        date_from: dateFrom,
      })
      if (dateTo) params.set('date_to', dateTo)

      const res = await fetchWithAuth(`/api/agents/email/auto-reply/generate-prompt?${params}`)
      if (!res.ok) throw new Error('Failed to fetch count')
      const data = await res.json()
      setEmailCountPreview({
        count: data.email_count,
        hasMore: data.has_more || false,
      })
    } catch (error) {
      console.error('Failed to fetch email count:', error)
      setEmailCountPreview(null)
    } finally {
      setIsLoadingCount(false)
    }
  }, [selectedInboxId, dateFrom, dateTo, fetchWithAuth])

  const handleStartAnalysis = async () => {
    if (!selectedInboxId) {
      toast.error('Please select an inbox')
      return
    }

    if (!dateFrom) {
      toast.error('Please select a start date')
      return
    }

    setStep('prompt')
  }

  const handleUseCachedPrompt = () => {
    if (cachedPrompt) {
      setGeneratedPrompt(cachedPrompt.prompt)
      setGeneratedStats(cachedPrompt.stats)
      setStep('result')
    }
  }

  const handleGeneratePrompt = async () => {
    try {
      setIsGenerating(true)

      const res = await fetchWithAuth('/api/agents/email/auto-reply/generate-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inbox_id: selectedInboxId,
          date_from: dateFrom,
          date_to: dateTo || undefined,
          max_emails: maxEmails ? parseInt(maxEmails) : 1000,
          system_prompt: systemPrompt,
        }),
      })

      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.error || 'Failed to generate prompt')
      }

      const data = await res.json()
      setGeneratedPrompt(data.prompt)
      setGeneratedStats(data.stats)

      // Update cached prompt
      setCachedPrompt({
        prompt: data.prompt,
        stats: data.stats,
        generatedAt: new Date().toISOString(),
        expiresAt: data.cached?.expiresAt || '',
        inboxId: selectedInboxId,
      })

      setStep('result')
      toast.success('Prompt generated successfully!')
    } catch (error) {
      console.error('Failed to generate prompt:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to generate prompt')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleApplyPrompt = () => {
    onApplyPrompt(generatedPrompt)
    toast.success('Prompt applied to Auto-Reply settings')
    handleClose()
  }

  const handleBackToConfig = () => {
    setStep('config')
  }

  const handleBackToPrompt = () => {
    setStep('prompt')
  }

  const handleClose = () => {
    setStep('config')
    onOpenChange(false)
  }

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins} min ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose} modal={false}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {(step === 'prompt' || step === 'result') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 mr-1"
                onClick={step === 'result' ? handleBackToPrompt : handleBackToConfig}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <Sparkles className="h-5 w-5" />
            <span>{step === 'config' && 'Generate Prompt from Emails'}</span>
            <span>{step === 'prompt' && 'Review System Prompt'}</span>
            <span>{step === 'result' && 'Generated Prompt'}</span>
          </DialogTitle>
          <DialogDescription>
            <span>{step === 'config' && 'Analyze email conversations to create an effective auto-reply prompt.'}</span>
            <span>{step === 'prompt' && 'Review the AI instructions that will be used to generate your prompt.'}</span>
            <span>{step === 'result' && 'Review the generated prompt and apply it to your auto-reply settings.'}</span>
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Configuration */}
        {step === 'config' && (
          <div className="space-y-6 py-4">
            {/* Cached Prompt Notice */}
            {cachedPrompt && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-green-700">Previously generated prompt available</span>
                    <div className="text-xs text-green-600 mt-1">
                      <span>Generated</span> {formatTimeAgo(cachedPrompt.generatedAt)} <span>•</span> {cachedPrompt.stats.threadsAnalyzed} <span>threads analyzed</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-4 border-green-300 text-green-700 hover:bg-green-100"
                    onClick={handleUseCachedPrompt}
                  >
                    Use Previous
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Inbox Selection */}
            <div className="space-y-2">
              <Label>Select Inbox</Label>
              {isLoadingInboxes ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading inboxes...</span>
                </div>
              ) : inboxes.length === 0 ? (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    No email inboxes found. Please connect an email inbox first.
                  </AlertDescription>
                </Alert>
              ) : (
                <Select value={selectedInboxId} onValueChange={setSelectedInboxId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an inbox" />
                  </SelectTrigger>
                  <SelectContent>
                    {inboxes.map((inbox) => (
                      <SelectItem key={inbox.id} value={inbox.id}>
                        {inbox.address}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Start Date *</span>
                </Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>End Date (optional)</span>
                </Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  min={dateFrom}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            {/* Email Count Preview */}
            {selectedInboxId && dateFrom && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <FileText className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-700">
                  {isLoadingCount ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Counting emails...
                    </span>
                  ) : emailCountPreview !== null ? (
                    <>
                      <strong>{emailCountPreview.count.toLocaleString()}{emailCountPreview.hasMore ? '+' : ''}</strong> emails found in selected period
                    </>
                  ) : (
                    'Select dates to see email count'
                  )}
                </span>
                {emailCountPreview !== null && !isLoadingCount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto h-6 px-2"
                    onClick={fetchEmailCount}
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                )}
              </div>
            )}

            {/* Max Emails */}
            <div className="space-y-2">
              <Label>Maximum Emails</Label>
              <Input
                type="number"
                placeholder="1000"
                value={maxEmails}
                onChange={(e) => setMaxEmails(e.target.value)}
                min={10}
                max={1000}
              />
              <p className="text-xs text-gray-500">
                Default: 1000. System will analyze emails to create an effective auto-reply prompt.
              </p>
            </div>

            {/* Info */}
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span>The AI will analyze your email conversations to create a prompt that:</span>
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                  <li>Matches your company&apos;s communication style</li>
                  <li>Includes relevant product/service information</li>
                  <li>Handles common customer inquiries effectively</li>
                  <li>Uses appropriate greetings and signatures</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Step 2: System Prompt Review */}
        {step === 'prompt' && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Settings2 className="h-4 w-4" />
              <span>
                Source: {promptSource === 'tenant' ? 'Tenant Config' : promptSource === 'global' ? 'Global Config' : 'Default'}
              </span>
              <Badge variant="outline" className="text-xs">
                Editable
              </Badge>
            </div>

            <div className="space-y-2">
              <Label>System Prompt for Generation</Label>
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
                placeholder="Enter system prompt for AI..."
              />
              <p className="text-xs text-gray-500">
                This prompt instructs the AI on how to analyze emails and generate an auto-reply prompt.
              </p>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Selected configuration:</strong>
                <ul className="list-disc list-inside mt-1">
                  <li><span>Inbox:</span> {inboxes.find(i => i.id === selectedInboxId)?.address}</li>
                  <li><span>Date range:</span> {dateFrom} <span>{dateTo ? `to ${dateTo}` : 'onwards'}</span></li>
                  <li><span>Max emails:</span> {maxEmails || '1000'}</li>
                </ul>
              </AlertDescription>
            </Alert>

            {/* Progress indicator for generating */}
            {isGenerating && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                  <div>
                    <div className="font-medium text-purple-700">Generating prompt...</div>
                    <div className="text-sm text-purple-600">
                      Analyzing emails and creating auto-reply prompt. This may take a minute.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Result */}
        {step === 'result' && (
          <div className="space-y-4 py-4">
            {/* Stats */}
            {generatedStats && (
              <div className="flex items-center gap-4 p-3 bg-green-50 rounded-lg text-sm">
                <div className="flex items-center gap-1 text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  <span className="font-medium">Generated successfully</span>
                </div>
                <div className="text-green-600">
                  {generatedStats.emailsProcessed} <span>emails •</span> {generatedStats.threadsAnalyzed} <span>threads analyzed</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Generated Auto-Reply Prompt</Label>
              <Textarea
                value={generatedPrompt}
                onChange={(e) => setGeneratedPrompt(e.target.value)}
                className="min-h-[350px] font-mono text-sm"
                placeholder="Generated prompt will appear here..."
              />
              <p className="text-xs text-gray-500">
                You can edit this prompt before applying it to your auto-reply settings.
              </p>
            </div>

            <Alert className="bg-blue-50 border-blue-200">
              <Mail className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-700">
                Click &quot;Apply to Auto-Reply&quot; to use this prompt in your auto-reply settings.
                The prompt will be saved and used for generating responses to customer emails.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter>
          {step === 'config' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleStartAnalysis}
                disabled={!selectedInboxId || !dateFrom || inboxes.length === 0}
              >
                Next: Review Prompt
              </Button>
            </>
          )}
          {step === 'prompt' && (
            <>
              <Button variant="outline" onClick={handleBackToConfig}>
                Back
              </Button>
              <Button
                onClick={handleGeneratePrompt}
                disabled={isGenerating || !systemPrompt.trim()}
              >
                {isGenerating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <span>{isGenerating ? 'Generating...' : 'Generate Prompt'}</span>
              </Button>
            </>
          )}
          {step === 'result' && (
            <>
              <Button variant="outline" onClick={handleBackToPrompt}>
                Regenerate
              </Button>
              <Button
                onClick={handleApplyPrompt}
                disabled={!generatedPrompt.trim()}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                <span>Apply to Auto-Reply</span>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
